import ecomInterceptor from "interceptors/ecomInterceptor";
import {
  IMockup,
  IMockupData,
  ISaveMockupPayload,
} from "Interfaces/interface.mockupGenerator";
import { IProductMockupResponse } from "Interfaces/interface.productsapi";
import toast from "react-hot-toast";
import { extractErrorMessage } from "utils/ErrorHandlers";

const mockupService = {
  async generateMockup(payload: any): Promise<Blob> {
    try {
      const response = await ecomInterceptor.post("/mockup/generate", payload, {
        headers: { "Content-Type": "application/json" },
        responseType: "blob",
      });
      toast.success("Mockup generated and saved to profile!");
      return response.data;
    } catch (error: any) {
      // Check if response has a blob error body
      if (
        error.response &&
        error.response.data instanceof Blob &&
        error.response.data.type === "application/json"
      ) {
        try {
          const errorText = await error.response.data.text(); // convert blob to string
          const errorJson = JSON.parse(errorText);
          console.error("Parsed JSON from Blob error:", errorJson);
          const message = extractErrorMessage({
            response: { data: errorJson },
          });
          toast.error(message);
          throw new Error(message);
        } catch (e) {
          console.error("Failed to parse error blob:", e);
          throw new Error("Failed to generate mockup.");
        }
      } else {
        const message = extractErrorMessage(error);
        toast.error(message);
        throw new Error("Failed to generate mockup.");
      }
    }
  },

  async fetchConfig(productId: string): Promise<IMockupData[]> {
    try {
      const response = await ecomInterceptor.get(`/configurator/${productId}`);
      if (!Array.isArray(response.data)) {
        throw new Error("Invalid configurator data format");
      }
      return response.data;
    } catch (error: any) {
      console.error(
        "Configurator ecomInterceptor error:",
        error?.response?.data || error.message
      );
      throw new Error("Failed to fetch configuration data");
    }
  },

  async fetchMockupConfigurations(id: string): Promise<IProductMockupResponse> {
    try {
      const response = await ecomInterceptor.get("/mockup/getProductById", {
        params: { productId: id },
      });
      const product = Array.isArray(response.data)
        ? response.data[0]
        : response.data;
      if (!product) {
        throw new Error("Product not found");
      }
      return {
        ...product,
        variants: product.variantList || [],
      };
    } catch (error: any) {
      console.error(
        "Error fetching product:",
        error?.response?.data || error.message
      );
      throw new Error("Product not found");
    }
  },

  async saveMockup(data: ISaveMockupPayload): Promise<void> {
    const formData = new FormData();
    const blob = new Blob([data.mockup], { type: "application/pdf" });

    formData.append("userId", data.userId);
    formData.append("mockup", blob);
    formData.append("mockupName", data.mockupName);

    try {
      const response = await ecomInterceptor.post("/mockup/save", formData);
    } catch (error: any) {
      console.error(
        "Mockup save error:",
        error?.response?.data || error.message
      );
      throw new Error("Failed to save mockup.");
    }
  },

  async userMockup(): Promise<IMockup[]> {
    try {
      const response = await ecomInterceptor.get("/users/get-user-mockups");
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(
        "Failed to fetch Mockups:",
        error?.response?.data || error.message
      );
      throw new Error("Failed to fetch Mockups");
    }
  },

  async deleteMockup(mockupId: string): Promise<{ updatedMockups: IMockup[] }> {
    try {
      const response = await ecomInterceptor.delete("/users/delete-mockup", {
        data: { mockupId },
      });
      return response.data;
    } catch (error: any) {
      console.error(
        "Failed to delete mockup:",
        error?.response?.data || error.message
      );
      throw new Error("Failed to delete mockup");
    }
  },

  async getConfiguredCombinations(npins: string[]): Promise<any> {
    try {
      const response = await ecomInterceptor.post(
        `/mockup/get-configured-combinations`,
        { NPINs: npins }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error));
    }
  },
};

export default mockupService;
